/**
 * @file     QuizCard.tsx
 * @readme   ../README.md
 * @purpose  One quiz question: 52px options at radius 16, the right answer on the success tint with a check, a wrong pick on the error tint, then one explanation line.
 */
'use client';

import '@/core/i18n/deferred/academy';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import type { QuizQuestion } from '@/content-system/academy/types';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { headingFont, hubColors, microLabelSx, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';

interface QuizCardProps {
  question: QuizQuestion;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  /** Sentence-case label above the question, e.g. "Check your understanding · 1 of 2". */
  label?: string;
}

const errorSoft = `color-mix(in srgb, ${hubColors.statusError} 10%, transparent)`;

const CheckGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const CrossGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
    <path d="M7 7l10 10M17 7L7 17" />
  </svg>
);

export function QuizCard({ question, selectedIndex, onSelect, label }: QuizCardProps) {
  const { t } = useTranslation();
  const isAnswered = selectedIndex !== null;
  const isCorrect = isAnswered && selectedIndex === question.correctIndex;
  const questionId = `quiz-${question.id}`;

  return (
    <Box component="fieldset" aria-labelledby={questionId} sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
      {label ? (
        <Box component="p" sx={{ ...microLabelSx, mb: 0.75 }}>
          {label}
        </Box>
      ) : null}
      <Box
        component="h3"
        id={questionId}
        sx={{ m: 0, mb: 2, fontFamily: headingFont, fontSize: { xs: 18, md: 20 }, fontWeight: fontWeightTokens.semibold, lineHeight: 1.3, color: hubColors.textPrimary }}
      >
        {question.question}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {question.options.map((option, idx) => {
          const right = isAnswered && idx === question.correctIndex;
          const wrong = isAnswered && idx === selectedIndex && !right;
          return (
            <Box
              key={idx}
              component="button"
              type="button"
              disabled={isAnswered}
              aria-pressed={idx === selectedIndex}
              onClick={() => onSelect(idx)}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                width: '100%',
                minHeight: 52,
                px: 2,
                py: 1.25,
                borderRadius: `${layoutTokens.radius.md}px`,
                border: `1px solid ${right ? hubColors.statusSuccess : wrong ? hubColors.statusError : hubColors.borderStrong}`,
                backgroundColor: right ? hubColors.successSoft : wrong ? errorSoft : hubColors.surfaceCard,
                color: hubColors.textPrimary,
                fontSize: 15,
                fontWeight: right ? fontWeightTokens.medium : fontWeightTokens.regular,
                lineHeight: 1.4,
                cursor: isAnswered ? 'default' : 'pointer',
                transition: transition('border-color', 'background-color'),
                '@media (hover: hover)': { '&:not(:disabled):hover': { borderColor: hubColors.interactivePrimary } },
                '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
                '& svg': { flexShrink: 0, color: right ? hubColors.statusSuccess : hubColors.statusError },
                ...reducedMotionSx,
              }}
            >
              <span>{option}</span>
              {right ? <CheckGlyph /> : wrong ? <CrossGlyph /> : null}
            </Box>
          );
        })}
      </Box>

      {isAnswered ? (
        <Box component="p" role="status" sx={{ m: 0, mt: 1.5, fontSize: 14, lineHeight: 1.55, color: hubColors.textSecondary }}>
          <Box component="span" sx={{ fontWeight: fontWeightTokens.medium, color: isCorrect ? hubColors.statusSuccess : hubColors.statusError }}>
            {isCorrect
              ? t('academy.quiz.right', { defaultValue: 'Right.' })
              : t('academy.quiz.notQuite', { defaultValue: 'Not quite.' })}
          </Box>{' '}
          {question.explanation}
        </Box>
      ) : null}
    </Box>
  );
}
